import React from "react";
import { flexRender, Row as TanStackRow } from "@tanstack/react-table";
import { StyledTbody, StyledTr, StyledTd, CheckboxCell } from "./styles";
import { LoadingSkeleton } from "./LoadingSkeleton";
import { EmptyState } from "./EmptyState";

interface TableBodyProps {
  rows: TanStackRow<any>[];
  loading?: boolean;
  emptyContent?: React.ReactElement;
  columnCount: number;
  handleRowClick?: (row: TanStackRow<any>) => unknown;
}

export const TableBody: React.FC<TableBodyProps> = ({
  rows,
  loading,
  emptyContent,
  columnCount,
  handleRowClick,
}) => {
  // Show loading skeleton
  if (loading) {
    return (
      <StyledTbody>
        <LoadingSkeleton columnCount={columnCount} rowCount={20} />
      </StyledTbody>
    );
  }

  // Show empty state if no rows
  if (rows.length === 0) {
    return (
      <StyledTbody>
        <EmptyState content={emptyContent} columnCount={columnCount} />
      </StyledTbody>
    );
  }

  // Render rows
  return (
    <StyledTbody>
      {rows.map((row) => {
        const isClickable = !!handleRowClick;
        const isSelected = row.getIsSelected();

        return (
          <StyledTr
            key={row.id}
            $clickable={isClickable}
            $selected={isSelected}
            onClick={
              isClickable
                ? (e) => {
                    // Don't trigger row click if clicking on checkbox
                    const target = e.target as HTMLElement;
                    if (
                      target.tagName === "INPUT" ||
                      target.closest('input[type="checkbox"]')
                    ) {
                      return;
                    }
                    handleRowClick(row);
                  }
                : undefined
            }
          >
            {row.getVisibleCells().map((cell) => {
              // Special handling for checkbox column
              const isCheckbox = cell.column.id === "select";

              const CellComponent = isCheckbox ? CheckboxCell : StyledTd;

              return (
                <CellComponent
                  key={cell.id}
                  onClick={
                    isCheckbox
                      ? (e) => {
                          // Prevent row click when clicking checkbox
                          e.stopPropagation();
                        }
                      : undefined
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </CellComponent>
              );
            })}
          </StyledTr>
        );
      })}
    </StyledTbody>
  );
};
